import React from 'react';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, SkipForward, User } from 'lucide-react';
import BiographyEditor from '@/components/BiographyEditor';
import SmartWritingAssistant from '@/components/SmartWritingAssistant';
import BiographyQualityPanel from '@/components/BiographyQualityPanel';

interface BiographyStepProps {
  biography: string;
  setBiography: (value: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  onCancel: () => void;
  categoryId: string;
}

const BiographyStep: React.FC<BiographyStepProps> = ({
  biography,
  setBiography,
  onContinue,
  onSkip,
  onCancel,
  categoryId
}) => {
  const maxCharacters = 3000;
  const wordCount = biography.trim().split(/\s+/).filter(Boolean).length;

  const handleInsertText = (text: string) => {
    const separator = biography && !biography.endsWith(' ') && !biography.endsWith('\n') ? ' ' : '';
    setBiography(biography + separator + text);
  };

  // No-op for save since we handle saving on continue
  const handleSave = () => {};

  return (
    <div
      data-overlay="biography-step"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      {/* Close Button */}
      <div className="absolute top-4 right-4 z-[101]">
        <Button variant="secondary" size="sm" onClick={onCancel} aria-label="Close biography step">
          <X className="h-4 w-4 mr-1" />
          Close
        </Button>
      </div>

      {/* Main Content */}
      <div
        className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-background rounded-2xl shadow-2xl border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b p-6 z-10">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Review Your Profile</h2>
              <p className="text-muted-foreground">
                Share your story to create deeply personalized sessions. You can update your biography or skip to continue.
              </p>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Editor */}
            <div className="lg:col-span-2 space-y-4">
              <BiographyEditor
                biography={biography}
                setBiography={setBiography}
                saving={false}
                onSave={handleSave}
                wordCount={wordCount}
                maxCharacters={maxCharacters}
              />
            </div>

            {/* Right Column - Assistants */}
            <div className="lg:col-span-1 space-y-4">
              <SmartWritingAssistant
                biography={biography}
                onInsertText={handleInsertText}
              />
              <BiographyQualityPanel
                biography={biography}
                categoryId={categoryId}
                onBiographyChange={setBiography}
              />
            </div>
          </div>
        </div>

        {/* Footer Actions - Prominent CTA */}
        <div className="sticky bottom-0 bg-gradient-to-t from-background via-background to-background/95 backdrop-blur-sm border-t-2 border-primary/20 p-6 z-10 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="ghost"
              onClick={onSkip}
              className="text-muted-foreground hover:text-foreground"
            >
              <SkipForward className="h-4 w-4 mr-2" />
              Skip for now
            </Button>

            <Button
              onClick={onContinue}
              disabled={biography.length > maxCharacters}
              size="lg"
              className="min-w-[280px] h-14 text-lg font-bold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300"
            >
              <ArrowRight className="h-5 w-5 mr-2" />
              Continue to Session Setup
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiographyStep;
